import { PageTitleWithBackButton } from "@/core/components/PageBackButton";
import { useMarketplaceMenuSyncMutation } from "@/generated/graphql";
import {
	Badge,
	Button,
	Can,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	SectionBuilder,
	type SectionNode,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	toast,
	useDialogController,
} from "@k3-tech/react-kit";
import {
	BanIcon,
	EllipsisIcon,
	ListIcon,
	ListTreeIcon,
	PackageIcon,
	RefreshCwIcon,
	StoreIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { UnavailableItemContainer } from "../containers/UnavailableItemContainer";
import type { SalesChannelCategoryGroupRow } from "../usecases/useSalesChannelCategoryGroupList";
import { useSalesChannelCategoryList } from "../usecases/useSalesChannelCategoryList";
import { BranchSalesChannelCategoryGroupTable } from "./BranchSalesChannelCategoryGroupTable";
import { SalesChannelCategoryGroupTree } from "./SalesChannelCategoryGroupTree";
import { SalesChannelCategoryTable } from "./SalesChannelCategoryTable";
import { SalesChannelGroupProductTable } from "./SalesChannelGroupProductTable";

export const SALES_CHANNEL_CATEGORY_GROUP_DETAIL_TABS = [
	"categories",
	"products",
	"branches",
	"unavailable",
] as const;

export type SalesChannelCategoryGroupDetailTab =
	(typeof SALES_CHANNEL_CATEGORY_GROUP_DETAIL_TABS)[number];

export interface SalesChannelCategoryGroupDetailProps {
	data: SalesChannelCategoryGroupRow;
	onEdit: () => void;
	onDelete: () => Promise<void>;
	onBack: () => void;
	activeTab: SalesChannelCategoryGroupDetailTab;
	onChangeTab: (tab: SalesChannelCategoryGroupDetailTab) => void;
}

export const SalesChannelCategoryGroupDetail = ({
	data,
	onEdit,
	onDelete,
	onBack,
	activeTab,
	onChangeTab,
}: SalesChannelCategoryGroupDetailProps) => {
	const { confirm } = useDialogController();
	const groupID = data.salesChannelCategoryGroupID;
	// Cashiers open this page to check their own menu, so the tree is the landing view.
	const [view, setView] = useState<"table" | "tree">("tree");
	const [, syncMenu] = useMarketplaceMenuSyncMutation();
	const [syncing, setSyncing] = useState(false);

	// Empty salesChannelID fans out over every store bound to this group (all channels).
	const onSyncMenu = async () => {
		setSyncing(true);
		try {
			const result = await syncMenu({
				input: { salesChannelID: "", salesChannelCategoryGroupID: groupID },
			});
			if (result.error) {
				toast.error(result.error.message);
				return;
			}
			const synced = result.data?.marketplaceMenuSync;
			toast.success(
				synced
					? `Synced ${synced.syncedCount}/${synced.totalCount}`
					: "Menu sync sent",
			);
		} finally {
			setSyncing(false);
		}
	};

	// Options loader (not a table): feeds the product category filter + name map.
	const categories = useSalesChannelCategoryList(groupID);

	const categoryOptions = useMemo(
		() =>
			categories.items.map((c) => ({
				value: c.salesChannelCategoryID,
				label: c.salesChannelCategoryName,
			})),
		[categories.items],
	);
	const categoryNameByID = useMemo(
		() =>
			new Map(
				categories.items.map((c) => [
					c.salesChannelCategoryID,
					c.salesChannelCategoryName,
				]),
			),
		[categories.items],
	);

	const onChanged = () => categories.refetch();

	const sections: SectionNode[] = [
		{
			id: "summary",
			variant: "card",
			layout: "grid",
			grid: { cols: 1, gap: "gap-6" },
			children: [
				{
					key: "header",
					content: (
						<div className="flex flex-col sm:flex-row items-start justify-between gap-4">
							<div className="min-w-0 flex-1">
								<div className="text-xl sm:text-2xl font-semibold leading-none break-words">
									<PageTitleWithBackButton
										title={data.salesChannelCategoryGroupName}
										onBack={onBack}
									/>
								</div>
								<div className="mt-2 flex flex-wrap gap-2">
									<Badge
										variant="secondary"
										className={data.isActive ? "text-green-600" : undefined}
									>
										{data.isActive ? "Active" : "Inactive"}
									</Badge>
									<Badge variant="secondary">
										{data.isUseMasterdataAvailability
											? "Masterdata availability"
											: "Manual availability"}
									</Badge>
								</div>
							</div>
							<div className="flex flex-wrap items-center gap-2">
								<Can permissions="branch_sales_channel_merchant_maps.edit">
									<Button
										variant="outline"
										onClick={onSyncMenu}
										disabled={syncing}
									>
										<RefreshCwIcon
											className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`}
										/>
										{syncing ? "Syncing…" : "Sync Menu"}
									</Button>
								</Can>
								<Can permissions="sales_channel_category_groups.edit">
									<Button onClick={onEdit}>Edit</Button>
								</Can>
								<Can permissions="sales_channel_category_groups.delete">
									<DropdownMenu>
										<DropdownMenuTrigger asChild>
											<Button variant="outline">
												<EllipsisIcon className="h-4 w-4" />
											</Button>
										</DropdownMenuTrigger>
										<DropdownMenuContent>
											<DropdownMenuItem
												onSelect={async () => {
													const ok = await confirm({
														title: `Delete ${data.salesChannelCategoryGroupName}?`,
														description: "This action cannot be undone.",
														destructive: true,
														confirmText: "Delete",
														cancelText: "Cancel",
													});
													if (!ok) return;
													await onDelete();
												}}
											>
												Delete
											</DropdownMenuItem>
										</DropdownMenuContent>
									</DropdownMenu>
								</Can>
							</div>
						</div>
					),
				},
			],
		},
	];

	return (
		<>
			<SectionBuilder sections={sections} />
			<div className="mb-4" />
			{view === "tree" ? (
				<SalesChannelCategoryGroupTree
					groupID={groupID}
					categoryOptions={categoryOptions}
					onShowTable={() => setView("table")}
					onChanged={onChanged}
				/>
			) : (
				<>
					<div className="mb-3 flex justify-end">
						<Button variant="outline" onClick={() => setView("tree")}>
							<ListTreeIcon className="h-4 w-4" />
							Show Tree
						</Button>
					</div>
					<Tabs
						value={activeTab}
						onValueChange={(value) =>
							onChangeTab(value as SalesChannelCategoryGroupDetailTab)
						}
					>
						<TabsList>
							<TabsTrigger value="categories">
								<ListIcon className="h-4 w-4 mr-1.5" />
								Categories
							</TabsTrigger>
							<TabsTrigger value="products">
								<PackageIcon className="h-4 w-4 mr-1.5" />
								Products
							</TabsTrigger>
							<TabsTrigger value="branches">
								<StoreIcon className="h-4 w-4 mr-1.5" />
								Branches
							</TabsTrigger>
							<TabsTrigger value="unavailable">
								<BanIcon className="h-4 w-4 mr-1.5" />
								Unavailable
							</TabsTrigger>
						</TabsList>
						<TabsContent value="categories">
							<SalesChannelCategoryTable
								groupID={groupID}
								onChanged={onChanged}
							/>
						</TabsContent>
						<TabsContent value="products">
							<SalesChannelGroupProductTable
								groupID={groupID}
								categoryOptions={categoryOptions}
								categoryNameByID={categoryNameByID}
								onChanged={onChanged}
							/>
						</TabsContent>
						<TabsContent value="branches">
							<BranchSalesChannelCategoryGroupTable
								groupID={groupID}
								groupName={data.salesChannelCategoryGroupName}
							/>
						</TabsContent>
						<TabsContent value="unavailable">
							<UnavailableItemContainer groupID={groupID} />
						</TabsContent>
					</Tabs>
				</>
			)}
		</>
	);
};
